import { UserRole } from '../../auth/types/auth.types';
import type { AlcanceNegocio } from '../../negocio/negocio.types';

/**
 * Reglas de la pantalla Usuarios para sesiones, baja y perfiles (Consulta CM,
 * fases 0 y 3). Son las MISMAS listas que la API
 * (`sesiones.domain.ts` CUENTAS_COMPARTIDAS y `perfiles.domain.ts`
 * ROLES_CON_PERFIL): aquí sólo deciden qué se ofrece; quien manda es la API.
 */

/** Cuentas compartidas: cerrarles las sesiones saca a TODOS los equipos de la tienda. */
export const ROLES_COMPARTIDOS: readonly UserRole[] = [UserRole.BRANCH_OFFICE, UserRole.WAREHOUSE_STAFF];

/** Roles que llevan perfil; los demás se quedan en Venta Asistida (409 ROL_SIN_PERFIL). */
export const ROLES_CON_PERFIL: readonly UserRole[] = [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.INDICADORES];

export const esCuentaCompartida = (rol: UserRole | string | null | undefined): boolean =>
  !!rol && (ROLES_COMPARTIDOS as readonly string[]).includes(rol);

export const admitePerfil = (rol: UserRole | string | null | undefined): boolean =>
  !!rol && (ROLES_CON_PERFIL as readonly string[]).includes(rol);

// ───────────────────────── Último acceso ─────────────────────────

export const DIAS_SIN_USO = 30;
const DIA_MS = 24 * 60 * 60 * 1000;
const ZONA = 'America/Mexico_City';

const diaMx = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const horaMx = (d: Date) =>
  new Intl.DateTimeFormat('es-MX', { timeZone: ZONA, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);

const fecha = (v: string | Date | null | undefined): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * «Hoy 08:12», «Ayer 21:40», «Hace 41 días» (hora de México). La API anota el
 * acceso como mucho una vez cada 5 min por cuenta, así que «Hoy 08:12» puede
 * ser de hasta 5 min antes.
 */
export function etiquetaUltimoAcceso(v: string | Date | null | undefined, ahora: Date = new Date()): string {
  const d = fecha(v);
  if (!d) return 'Sin registro';
  const hoy = diaMx(ahora);
  const ayer = diaMx(new Date(ahora.getTime() - DIA_MS));
  const dia = diaMx(d);
  if (dia === hoy) return `Hoy ${horaMx(d)}`;
  if (dia === ayer) return `Ayer ${horaMx(d)}`;
  const dias = Math.max(1, Math.floor((ahora.getTime() - d.getTime()) / DIA_MS));
  return `Hace ${dias} día${dias === 1 ? '' : 's'}`;
}

/**
 * Sin uso = con un acceso REGISTRADO de hace 30 días o más. Sin registro no
 * cuenta: `ultimo_acceso` nace vacío al desplegar y marcaría a todas las
 * cuentas como «sin uso» (decisión por omisión, preguntas.md).
 */
export function sinUso(u: { ultimoAcceso?: string | Date | null }, ahora: Date = new Date(), dias = DIAS_SIN_USO): boolean {
  const d = fecha(u.ultimoAcceso);
  return !!d && ahora.getTime() - d.getTime() >= dias * DIA_MS;
}

// ───────────────────────── Motivo ─────────────────────────

/** Los de la maqueta. Quedan en la bitácora de la cuenta. */
export const MOTIVOS = [
  'Rotación de personal',
  'Equipo perdido o robado',
  'Sospecha de acceso indebido',
  'Cambio de puesto',
  'Otro',
] as const;

export const MOTIVO_MAX = 200;

export function armarMotivo(motivo: string, nota: string): string {
  const n = nota.trim().replace(/\s+/g, ' ');
  return (n ? `${motivo} · ${n}` : motivo).slice(0, MOTIVO_MAX);
}

// ───────────────────────── Alcances ─────────────────────────

export function etiquetaAlcanceNegocio(a: AlcanceNegocio): string {
  switch (a.tipo) {
    case 'todas':
      return 'Todas las tiendas';
    case 'direccion':
      return 'Dirección (todas)';
    case 'cadena':
      return `Cadena ${a.cadena}`;
    case 'regional':
      return `Regional ${a.nombre}`;
    case 'supervisor':
      return `Supervisor ${a.nombre}`;
    case 'tienda':
      return `Tienda ${a.cadena} ${a.num}`;
    default:
      return '—';
  }
}

/** Alcance de Metas tal como lo trae `GET /metas-colaborador/accesos`. */
export interface AlcanceMetasMin {
  tipo: string;
  nombre: string;
}

const etiquetaMetas = (a: AlcanceMetasMin) =>
  a.tipo === 'direccion'
    ? 'Dirección (todas)'
    : a.tipo === 'regional'
      ? `Regional ${a.nombre}`
      : a.tipo === 'supervisor'
        ? `Supervisor ${a.nombre}`
        : `${a.tipo} ${a.nombre}`;

const unicos = (xs: string[]) => xs.filter((x, i) => xs.indexOf(x) === i);

/** «Metas: … · Consulta CM: …». Las dos fuentes van por separado: dan cosas distintas. */
export function textoAlcances(metas: AlcanceMetasMin[], negocio: AlcanceNegocio[]): string {
  const partes: string[] = [];
  const m = unicos(metas.map(etiquetaMetas));
  const n = unicos(negocio.map(etiquetaAlcanceNegocio));
  if (m.length) partes.push(`Metas: ${m.join(', ')}`);
  if (n.length) partes.push(`Consulta CM: ${n.join(', ')}`);
  return partes.length ? partes.join(' · ') : '—';
}
